import { DATE_FORMAT_SHORT_FNS, GetGroupAttendanceAggregateResponse } from '@schooly/api';
import { format } from 'date-fns';

import {
  AggregateCell,
  AggregateWeekDay,
  compareMembers,
  GroupGridMember,
  GroupGridResult,
  hasTrackingPointEntry,
  isDayWithinMembership,
  parseDate,
} from './buildGroupGrid';
import {
  buildTrackingPoints,
  findCellForPoint,
  pickEntryForPoint,
  trackingPointKey,
} from './trackingPoints';
import {
  AttendanceGridCell,
  AttendanceGridCellValue,
  AttendanceGridColumn,
  AttendanceGridTrackingPoint,
} from './types';

type AggregatePages = GetGroupAttendanceAggregateResponse[];

export type BuildSubjectGroupRowsInput = {
  members: GroupGridMember[];
  pages: AggregatePages;
  columns: SubjectGridColumn[];
};

type RegisterWeekDay = AggregateWeekDay & { register_id: string };
export type SubjectGridColumn = AttendanceGridColumn & {
  date: string;
  trackingPoint: AttendanceGridTrackingPoint;
  registerId: string;
};

const isRegisterWeekDay = (weekDay: AggregateWeekDay): weekDay is RegisterWeekDay =>
  !!weekDay.register_id;

/** Registers with a real register_id, oldest register_date first. */
const orderedRegisters = (pages: AggregatePages): RegisterWeekDay[] =>
  pages
    .flatMap((page) => page.total.week_days)
    .filter(isRegisterWeekDay)
    .sort((a, b) => a.register_date.localeCompare(b.register_date));

const trackingPointColumnKey = (registerId: string, point: AttendanceGridTrackingPoint): string =>
  `${registerId}:${trackingPointKey(point.session, point.period?.id ?? null)}`;

const trackingPointLabel = (
  registerDate: string,
  trackingPoint: AttendanceGridTrackingPoint,
): string => `${format(parseDate(registerDate), DATE_FORMAT_SHORT_FNS)} ${trackingPoint.label}`;

const sessionNotice = (
  cell: AggregateCell,
  trackingPoint: AttendanceGridTrackingPoint,
): Pick<AttendanceGridCellValue, 'justification' | 'absence_request'> =>
  trackingPoint.session
    ? { justification: cell.value.justification, absence_request: cell.value.absence_request }
    : { justification: null, absence_request: null };

const periodCellValue = (
  periodId: string,
  cell: AggregateCell,
  trackingPoint: AttendanceGridTrackingPoint,
): AttendanceGridCellValue => ({
  ...sessionNotice(cell, trackingPoint),
  am: null,
  pm: null,
  periods: { [periodId]: pickEntryForPoint(cell.value.attendance_entries, trackingPoint) },
});

const sessionCellValue = (
  cell: AggregateCell,
  trackingPoint: AttendanceGridTrackingPoint,
): AttendanceGridCellValue => {
  const entry = pickEntryForPoint(cell.value.attendance_entries, trackingPoint);

  return {
    ...sessionNotice(cell, trackingPoint),
    am: trackingPoint.session === 'am' ? entry : null,
    pm: trackingPoint.session === 'pm' ? entry : null,
  };
};

const trackingPointCellValue = (
  trackingPoint: AttendanceGridTrackingPoint,
  cell: AggregateCell,
): AttendanceGridCellValue => {
  const periodId = trackingPoint.period?.id;
  if (periodId) return periodCellValue(periodId, cell, trackingPoint);
  return sessionCellValue(cell, trackingPoint);
};

const buildTrackingPointColumns = (registers: RegisterWeekDay[]): SubjectGridColumn[] =>
  registers.flatMap((register) =>
    buildTrackingPoints(register.tracking_points).map((trackingPoint) => ({
      key: trackingPointColumnKey(register.register_id, trackingPoint),
      date: register.register_date,
      label: trackingPointLabel(register.register_date, trackingPoint),
      sessions: [],
      trackingPoint,
      registerId: register.register_id,
      has_register: true,
      is_future: false,
    })),
  );

const buildMemberCells = (
  member: GroupGridMember,
  columns: SubjectGridColumn[],
  cellsByRegisterId: Map<string, AggregateCell[]>,
): AttendanceGridCell[] =>
  columns.map((column) => {
    if (!isDayWithinMembership(parseDate(column.date), member.memberships)) {
      return { column_key: column.key, kind: 'out_of_day', editable: false };
    }

    const cell = findCellForPoint(cellsByRegisterId.get(column.registerId), column.trackingPoint);
    if (!cell) {
      return { column_key: column.key, kind: 'blocked', editable: false };
    }

    const value = trackingPointCellValue(column.trackingPoint, cell);

    return {
      column_key: column.key,
      kind: 'normal',
      editable: hasTrackingPointEntry(value),
      data: {
        register_id: column.registerId,
        date: column.date,
        relation_id: member.relation_id,
        cell: value,
      },
    };
  });

export const selectMembersForColumns = (
  members: GroupGridMember[],
  columns: SubjectGridColumn[],
): GroupGridMember[] =>
  members.filter((member) =>
    columns.some((column) => isDayWithinMembership(parseDate(column.date), member.memberships)),
  );

const cellsByRegister = (cells: AggregateCell[] | undefined): Map<string, AggregateCell[]> => {
  const grouped = new Map<string, AggregateCell[]>();
  for (const cell of cells ?? []) {
    const list = grouped.get(cell.meta.register_id) ?? [];
    list.push(cell);
    grouped.set(cell.meta.register_id, list);
  }
  return grouped;
};

export const buildSubjectGroupColumns = (pages: AggregatePages): SubjectGridColumn[] =>
  buildTrackingPointColumns(orderedRegisters(pages));

export const buildSubjectGroupRows = ({
  members,
  pages,
  columns,
}: BuildSubjectGroupRowsInput): GroupGridResult['rows'] => {
  const cellsByRelationId = new Map<string, AggregateCell[]>();
  for (const { meta, cells } of pages.flatMap((page) => page.rows)) {
    cellsByRelationId.set(meta.relation_id, [
      ...(cellsByRelationId.get(meta.relation_id) ?? []),
      ...cells,
    ]);
  }

  return selectMembersForColumns(members, columns)
    .sort(compareMembers)
    .map((member) => ({
      relation_id: member.relation_id,
      student: member,
      cells: buildMemberCells(
        member,
        columns,
        cellsByRegister(cellsByRelationId.get(member.relation_id)),
      ),
    }));
};

/**
 * Placeholder grid rendered while the register aggregate is still loading: every
 * group member (sorted) plus `columnCount` loading columns whose cells render as
 * skeletons, so the whole table shape paints immediately.
 */
export const buildSubjectGroupSkeleton = (
  members: GroupGridMember[],
  columnCount: number,
): GroupGridResult => {
  const columns: AttendanceGridColumn[] = Array.from({ length: columnCount }, (_, index) => ({
    key: `skeleton-${index}`,
    date: null,
    label: '',
    sessions: [],
    has_register: false,
    is_future: false,
    is_loading: true,
  }));

  const rows = [...members].sort(compareMembers).map((member) => ({
    relation_id: member.relation_id,
    student: member,
    cells: columns.map<AttendanceGridCell>((column) => ({
      column_key: column.key,
      kind: 'loading' as const,
      editable: false,
    })),
  }));

  return { columns, rows };
};
